import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { LpCanvasService } from '@core/lp/lp-canvas.service';
import { CanvasRendererComponent } from '@features/canvas/canvas-renderer/canvas-renderer.component';
import { ICanvasElement } from '@features/canvas/canvas.types';
import { LpActionsService } from '@features/lp/lp-actions/lp-actions.service';
import { LpTriggersService } from '@features/lp/lp-triggers/lp-triggers.service';
import { useElementSize } from '@shared/angular.utils';
import { workaroundCanvasRatio } from '@shared/workarounds.utils';
import { LpClassicMap } from '@typings/lp/generated';
import { Id } from '@typings/id';

@Component({
  selector: 'lp-classic-map',
  imports: [CanvasRendererComponent],
  templateUrl: './lp-classic-map.component.html',
  styleUrl: './lp-classic-map.component.css',
})
export class LpClassicMapComponent {
  map = input.required<LpClassicMap>();
  #lpActionsService = inject(LpActionsService);
  #lpCanvasService = inject(LpCanvasService);
  #lpTriggersService = inject(LpTriggersService);

  elements = signal<ICanvasElement[]>([]);
  size = computed(() => workaroundCanvasRatio(this.map().canvasRatio));
  sizeInPixels = useElementSize();

  constructor() {
    effect(async () => {
      this.#lpCanvasService.subscribeToShowIfChanges();

      this.elements.set([
        await this.#lpCanvasService.convertBackground(this.map().background, this.size()),
        ...(await this.#lpCanvasService.convertVisualElements(this.map().visualElements, this.sizeInPixels())),
      ]);
    });

    effect(async () => {
      const map = this.map();

      queueMicrotask(async () => {
        await this.#lpTriggersService.on(map.onTrigger);
        await this.#lpActionsService.perform(map.onActivate);
      });
    });
  }

  public async handleClick(uid: Id) {
    const visualElement = this.map().visualElements.find((ve) => ve.uid === uid);
    if (!visualElement) {
      throw new Error(`Visual element '${uid}' not found`);
    }

    if (visualElement.onClick) {
      await this.#lpActionsService.perform(visualElement.onClick);
    }
  }
}
